import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
  addDoc,
  serverTimestamp,
  getDocs,
} from 'firebase/firestore';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { RoutePlanner } from '../../components/maps/RoutePlanner';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import { db } from '../../config/firebase';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { RequestPassengerModal } from '../../components/flexy-ride/RequestPassengerModal';

export default function FlexyRidePassenger() {
  const PAGE_SIZE = 12;
  const { user, initializing } = usePublicAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ownerInfo, setOwnerInfo] = useState({});
  const [showPassengerModal, setShowPassengerModal] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [appliedListingIds, setAppliedListingIds] = useState(new Set());
  const [acceptedListingIds, setAcceptedListingIds] = useState(new Set());

  const showLoginGate = !initializing && !user;

  const queryFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      from: { description: params.get('from') || '' },
      to: { description: params.get('to') || '' },
      departureDate: params.get('date') || '',
    };
  }, [location.search]);

  const [routeFilters, setRouteFilters] = useState(queryFilters);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setRouteFilters(queryFilters);
  }, [queryFilters]);

  // Listen accepted/completed ride_requests to exclude their listings
  useEffect(() => {
    const acceptedIdsRef = { current: new Set() };
    const completedIdsRef = { current: new Set() };

    const updateCombinedIds = () => {
      const combined = new Set([
        ...acceptedIdsRef.current,
        ...completedIdsRef.current,
      ]);
      setAcceptedListingIds(combined);
    };

    const unsubAccepted = onSnapshot(
      query(collection(db, 'ride_requests'), where('status', '==', 'accepted')),
      snapshot => {
        acceptedIdsRef.current = new Set();
        if (snapshot?.docs) {
          snapshot.docs.forEach(doc => {
            const listingId = doc.data()?.listingId;
            if (listingId) {
              acceptedIdsRef.current.add(listingId);
            }
          });
        }
        updateCombinedIds();
      },
    );

    const unsubCompleted = onSnapshot(
      query(
        collection(db, 'ride_requests'),
        where('status', '==', 'completed'),
      ),
      snapshot => {
        completedIdsRef.current = new Set();
        if (snapshot?.docs) {
          snapshot.docs.forEach(doc => {
            const listingId = doc.data()?.listingId;
            if (listingId) {
              completedIdsRef.current.add(listingId);
            }
          });
        }
        updateCombinedIds();
      },
    );

    return () => {
      unsubAccepted();
      unsubCompleted();
    };
  }, []);

  useEffect(() => {
    const colRef = collection(db, 'ride_listings');
    const q = query(
      colRef,
      where('mode', '==', 'ride'),
      where('type', '==', 'passenger'),
    );

    const unsub = onSnapshot(
      q,
      snapshot => {
        const data = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
          }))
          // Hide completed listings and those with accepted/completed requests
          .filter(
            l => l.status !== 'completed' && !acceptedListingIds.has(l.id),
          );

        // Newest listings first
        data.sort((a, b) => {
          // Handle Firestore Timestamp objects
          let aTime = 0;
          let bTime = 0;

          if (a.createdAt) {
            if (typeof a.createdAt.toMillis === 'function') {
              aTime = a.createdAt.toMillis();
            } else if (typeof a.createdAt === 'number') {
              aTime = a.createdAt;
            } else if (a.createdAt.seconds) {
              aTime =
                a.createdAt.seconds * 1000 +
                (a.createdAt.nanoseconds || 0) / 1000000;
            }
          }

          if (b.createdAt) {
            if (typeof b.createdAt.toMillis === 'function') {
              bTime = b.createdAt.toMillis();
            } else if (typeof b.createdAt === 'number') {
              bTime = b.createdAt;
            } else if (b.createdAt.seconds) {
              bTime =
                b.createdAt.seconds * 1000 +
                (b.createdAt.nanoseconds || 0) / 1000000;
            }
          }

          return bTime - aTime;
        });

        setListings(data);
        setLoading(false);
        setError('');
      },
      err => {
        console.error('Error loading Flexy Ride passenger listings', err);
        setError('Could not load Flexy Ride passenger listings.');
        setLoading(false);
      },
    );
    return () => unsub();
  }, [acceptedListingIds]);

  useEffect(() => {
    setCurrentPage(1);
  }, [routeFilters, listings.length]);

  // Fetch owner profiles (name + verification) similar to mobile screens
  useEffect(() => {
    const fetchOwners = async () => {
      const ids = Array.from(
        new Set(listings.map(l => l.createdBy).filter(Boolean)),
      );
      for (const id of ids) {
        if (ownerInfo[id]) continue;
        try {
          const userDoc = await getDoc(doc(db, 'users', id));
          const data = userDoc.data() || {};
          setOwnerInfo(prev => ({
            ...prev,
            [id]: {
              name:
                data.displayName ||
                data.username ||
                data.email?.split?.('@')?.[0] ||
                'Flexy user',
              verified: data.verificationStatus === 'verified',
            },
          }));
        } catch (err) {
          console.error(
            'Error fetching owner info for Flexy Ride passenger view:',
            err,
          );
        }
      }
    };

    if (listings.length > 0) {
      fetchOwners();
    }
  }, [listings, ownerInfo]);

  // Track listings current user already applied to
  useEffect(() => {
    if (!user?.uid) {
      setAppliedListingIds(new Set());
      return;
    }
    const unsub = onSnapshot(
      query(
        collection(db, 'ride_requests'),
        where('applicantId', '==', user.uid),
        where('status', 'in', [
          'pending',
          'accepted',
          'confirm_pending',
          'completed',
        ]),
      ),
      snapshot => {
        if (!snapshot?.docs) {
          setAppliedListingIds(new Set());
          return;
        }
        const ids = new Set(
          snapshot.docs.map(doc => doc.data()?.listingId).filter(Boolean),
        );
        setAppliedListingIds(ids);
      },
    );
    return () => unsub();
  }, [user?.uid]);

  const handleApply = async (listing, { price, carBrand, note }) => {
    if (!user?.uid) {
      alert('Please sign in to apply as a driver.');
      return;
    }

    if (user.uid === listing.createdBy) {
      alert('You cannot apply to your own listing.');
      return;
    }

    setSubmitLoading(true);
    try {
      // Check if already applied
      const existingSnapshot = await getDocs(
        query(
          collection(db, 'ride_requests'),
          where('listingId', '==', listing.id),
          where('applicantId', '==', user.uid),
        ),
      );

      const activeExists = existingSnapshot.docs.some(doc => {
        const status = doc.data()?.status;
        return status === 'pending' || status === 'accepted';
      });

      if (activeExists) {
        alert('You have already sent a request to this listing.');
        setAppliedListingIds(prev => {
          const next = new Set(prev);
          next.add(listing.id);
          return next;
        });
        setShowPassengerModal(false);
        setSelectedListing(null);
        return;
      }

      // Get listing owner name
      const listingOwnerName =
        ownerInfo[listing.createdBy]?.name ||
        user.displayName ||
        user.email?.split('@')[0] ||
        'User';

      // Create application
      await addDoc(collection(db, 'ride_requests'), {
        listingId: listing.id,
        listingOwnerId: listing.createdBy,
        listingOwnerName: listingOwnerName,
        applicantId: user.uid,
        applicantName: user.displayName || user.email?.split('@')[0] || 'User',
        applicantEmail: user.email,
        requesterId: user.uid,
        requesterName: user.displayName || user.email?.split('@')[0] || 'User',
        requesterEmail: user.email,
        tripOwnerId: listing.createdBy,
        tripOwnerName: listingOwnerName,
        status: 'pending',
        listingType: 'passenger',
        listingMode: listing.mode || 'ride',
        price: price || '',
        carBrand: carBrand || '',
        note: note || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      alert('Request sent successfully!');
      setAppliedListingIds(prev => {
        const next = new Set(prev);
        next.add(listing.id);
        return next;
      });
    } catch (error) {
      console.error('Error applying to listing:', error);
      alert('Failed to send request. Please try again.');
    } finally {
      setSubmitLoading(false);
      setShowPassengerModal(false);
      setSelectedListing(null);
    }
  };

  const filteredListings = useMemo(() => {
    const fromFilter = routeFilters.from?.description?.toLowerCase().trim();
    const toFilter = routeFilters.to?.description?.toLowerCase().trim();

    return listings.filter(listing => {
      if (fromFilter) {
        const fromValue =
          listing.from || listing.fromAddress?.city || listing.fromCity || '';
        if (!fromValue.toLowerCase().includes(fromFilter)) return false;
      }
      if (toFilter) {
        const toValue =
          listing.to || listing.toAddress?.city || listing.toCity || '';
        if (!toValue.toLowerCase().includes(toFilter)) return false;
      }
      return true;
    });
  }, [listings, routeFilters]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredListings.length / PAGE_SIZE),
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [filteredListings.length, currentPage, totalPages]);

  const paginatedListings = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredListings.slice(start, start + PAGE_SIZE);
  }, [filteredListings, currentPage]);

  const handleSearch = filters => {
    setRouteFilters(filters);
  };

  const formatRoute = listing => {
    if (listing.from && listing.to) return `${listing.from} → ${listing.to}`;
    if (listing.fromAddress?.city && listing.toAddress?.city) {
      return `${listing.fromAddress.city} → ${listing.toAddress.city}`;
    }
    if (listing.fromCity && listing.toCity) {
      return `${listing.fromCity} → ${listing.toCity}`;
    }
    return 'Route not set';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />
      <main className="mx-auto max-w-8xl px-4 pt-28 pb-16 sm:px-6 lg:px-8">
        <RoutePlanner
          title="Flexy Ride - I’m a Driver"
          subtitle="Find Passengers"
          ctaLabel="Search Passengers"
          onSearchChange={handleSearch}
          initialFilters={queryFilters}
        />

        <section className="mt-10">
          <h2 className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500 dark:text-slate-400">
            See active passengers below
          </h2>
          <div className="mt-4 space-y-6">
            {showLoginGate ? (
              <div className="rounded-3xl bg-white dark:bg-slate-900 p-6 text-sm text-gray-600 dark:text-slate-400 shadow-lg">
                <p className="font-semibold text-gray-900 dark:text-white">
                  Please sign in to view Flexy Ride drivers
                </p>
                <p className="mt-2 text-gray-500 dark:text-slate-400">
                  Use the same account as the mobile app to see synchronized
                  ride listings.
                </p>
                <Link
                  to="/auth/login"
                  className="mt-4 inline-flex rounded-2xl bg-black dark:bg-slate-700 px-5 py-3 text-sm font-semibold text-yellow-400 transition hover:bg-gray-900 dark:hover:bg-slate-800"
                >
                  Go to login
                </Link>
              </div>
            ) : loading ? (
              <div className="rounded-3xl bg-white dark:bg-slate-900 p-6 shadow-lg">
                <div className="h-4 w-1/3 animate-pulse rounded bg-gray-200 dark:bg-slate-800" />
                <div className="mt-6 space-y-3">
                  <div className="h-3 w-full animate-pulse rounded bg-gray-200 dark:bg-slate-800" />
                  <div className="h-3 w-11/12 animate-pulse rounded bg-gray-200 dark:bg-slate-800" />
                  <div className="h-3 w-2/3 animate-pulse rounded bg-gray-200 dark:bg-slate-800" />
                </div>
              </div>
            ) : error ? (
              <p className="rounded-3xl bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-600 dark:text-red-400">
                {error}
              </p>
            ) : filteredListings.length === 0 ? (
              <div className="rounded-3xl bg-white dark:bg-slate-900 p-6 shadow-lg text-sm text-gray-600 dark:text-slate-400">
                <p className="font-semibold text-gray-900 dark:text-white">
                  No Flexy Ride driver listings found right now.
                </p>
                <p className="mt-1">
                  You can still keep things moving—publish a seat or passenger
                  listing from the mobile app.
                </p>
              </div>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {paginatedListings.map(listing => (
                  <div
                    key={listing.id}
                    className="flex flex-col justify-between rounded-3xl bg-white p-5 text-sm shadow-lg ring-1 ring-gray-200/70 dark:bg-slate-900 dark:ring-slate-700"
                  >
                    <div className="space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-yellow-500">
                        Flexy Ride • Passenger listing
                      </p>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-bold text-gray-900 dark:text-white">
                          {ownerInfo[listing.createdBy]?.name || 'Flexy user'}
                        </p>
                        {ownerInfo[listing.createdBy] && (
                          <span
                            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                              ownerInfo[listing.createdBy]?.verified
                                ? 'bg-emerald-500/10 text-emerald-400'
                                : 'bg-amber-500/10 text-amber-300'
                            }`}
                          >
                            {ownerInfo[listing.createdBy]?.verified
                              ? 'Verified passenger'
                              : 'Unverified passenger'}
                          </span>
                        )}
                      </div>
                      <p className="line-clamp-2 text-base font-semibold text-gray-900 dark:text-white">
                        {formatRoute(listing)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-slate-400">
                        {listing.date || 'Date not set'} •{' '}
                        {listing.time || 'Time not set'}
                      </p>
                      {listing.price && (
                        <p className="text-sm font-semibold text-yellow-600 dark:text-yellow-400">
                          {listing.currency || 'CAD'} ${listing.price}
                        </p>
                      )}
                      {listing.seats && (
                        <p className="text-xs text-gray-600 dark:text-slate-300">
                          Seats requested:{' '}
                          <span className="font-semibold">{listing.seats}</span>
                        </p>
                      )}
                      {listing.carBrand && (
                        <p className="text-xs text-gray-600 dark:text-slate-300">
                          Preferred car:{' '}
                          <span className="font-semibold">
                            {listing.carBrand}
                            {listing.carModel ? ` ${listing.carModel}` : ''}
                          </span>
                        </p>
                      )}
                      {listing.note && (
                        <p className="text-xs italic text-gray-500 dark:text-slate-400 line-clamp-2">
                          “{listing.note}”
                        </p>
                      )}
                    </div>
                    <div className="mt-4 flex justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (!user) {
                            alert('Please sign in to apply as a driver.');
                            return;
                          }
                          if (user.uid === listing.createdBy) {
                            alert('You cannot apply to your own listing.');
                            return;
                          }
                          if (appliedListingIds.has(listing.id)) {
                            alert(
                              'You have already sent a request to this listing.',
                            );
                            return;
                          }
                          setSelectedListing(listing);
                          setShowPassengerModal(true);
                        }}
                        disabled={
                          !user ||
                          user.uid === listing.createdBy ||
                          appliedListingIds.has(listing.id)
                        }
                        className={`flex-1 rounded-2xl px-4 py-2 text-xs font-semibold transition ${
                          !user ||
                          user.uid === listing.createdBy ||
                          appliedListingIds.has(listing.id)
                            ? 'cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-slate-800 dark:text-slate-500'
                            : 'bg-black text-yellow-400 hover:bg-gray-900 dark:bg-yellow-500 dark:text-black dark:hover:bg-yellow-400'
                        }`}
                      >
                        {!user
                          ? 'Sign in to apply'
                          : user.uid === listing.createdBy
                          ? 'Your listing'
                          : appliedListingIds.has(listing.id)
                          ? 'Applied'
                          : 'Request ride'}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/flexy-ride/passenger/${listing.id}`)
                        }
                        className="flex-1 rounded-2xl border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-900 transition hover:border-gray-400 dark:border-slate-700 dark:text-slate-100 dark:hover:border-slate-500"
                      >
                        View details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {filteredListings.length > PAGE_SIZE && (
              <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="rounded-full border px-4 py-2 text-sm font-semibold transition disabled:opacity-40"
                >
                  Prev
                </button>
                {Array.from({ length: totalPages }, (_, index) => {
                  const page = index + 1;
                  return (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setCurrentPage(page)}
                      className={`h-10 w-10 rounded-full text-sm font-semibold transition ${
                        currentPage === page
                          ? 'bg-black text-yellow-400 dark:bg-yellow-500 dark:text-black'
                          : 'border border-gray-300 text-gray-700 dark:border-slate-700 dark:text-slate-200'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage(prev => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="rounded-full border px-4 py-2 text-sm font-semibold transition disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <RequestPassengerModal
        visible={showPassengerModal}
        onClose={() => {
          setShowPassengerModal(false);
          setSelectedListing(null);
        }}
        onSubmit={data => selectedListing && handleApply(selectedListing, data)}
        loading={submitLoading}
      />
    </div>
  );
}
